import React, { memo, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { WarningMessage } from '../utils_components.jsx';
import { ItemField } from './item_field.jsx';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

// The question card. Mirrors the design system's `.card` (rounded-2xl, hairline
// border, shadow-sm) — it replaces the old inline-styled box with its heavy grey
// drop shadow. `overflow-hidden` lets the title bar sit flush in the top corners.
export const FORM_CLASS =
  'relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm';

// Exported so the continuous-flow view can gate its single "Continue" control with
// exactly the same rule the per-item Next button uses.
export function checkMissing(item, values) {
  if (item.fields === undefined) return false;
  const newValues = values || Array(item.fields.length);
  for (let i = 0; i < item.fields.length; i++) {
    const f = item.fields[i];
    if (!f.optional && !f.defaultValue && f.type !== 'noInput' && f.type !== 'checkbox') {
      if (newValues === undefined || newValues[i] === undefined || newValues[i] === null) {
        return true;
      }
    }
  }
  return false;
}

function ItemComponent(props) {
  const {
    showApplyToAll,
    applyToAll,
    preliminarySection,
    values,
    preliminaries,
    item,
    loopIndex,
    isObserver,
    readOnly,
    onApplyToAll,
    onChange,
    onItemPreliminaryChange,
    onSubmit,
    loopType,
    // Continuous flow renders many items at once: it drives progression from a
    // single control at the end of the list, and must not let each item grab focus.
    showSubmit = true,
    autoFocusFirstField = true,
    // Continuous flow marks the item the engine sits on. Selection commits to
    // primary, per the design system's state convention.
    highlighted = false,
  } = props;
  const refItem = useRef(null);

  const newValues = values || (item.fields !== undefined ? Array(item.fields.length) : []);
  const newPreliminaries = preliminaries || (item.fields !== undefined ? Array(item.fields.length) : []);
  const fields = item.fields;
  const missingValues = !readOnly && checkMissing(item, values);
  const hasErrors = item.errors.length > 0;
  let itemKey = 'item' + item.id + '-';
  if (item.loopName && loopIndex !== undefined) {
    itemKey += item.loopName + '-' + loopIndex + '-';
  }
  const globalErrors = [];
  const fieldErrors = fields !== undefined ? Array(fields.length) : [];
  for (let i = 0; i < item.errors.length; i++) {
    const error = item.errors[i];
    if (error.field === undefined) {
      globalErrors.push(error);
    } else {
      fieldErrors[i] = error;
    }
  }

  let handleChange, handleAddition, togglePreliminary;
  if (readOnly) {
    handleChange = () => null;
    handleAddition = () => null;
    togglePreliminary = () => null;
  } else {
    handleChange = (fieldIndex, value) => onChange(fieldIndex, value);
    handleAddition = (fieldIndex, value) => {
      const current = newValues[fieldIndex];
      const existing = Array.isArray(current) ? current : current == null ? [] : [current];
      if (!existing.includes(value)) {
        onChange(fieldIndex, [...existing, value]);
      }
    };
    togglePreliminary = (fieldIndex) => {
      const preliminary = !newPreliminaries[fieldIndex];
      onItemPreliminaryChange(fieldIndex, preliminary);
    };
  }
  const actionableFields = fields.filter((field) => field.type !== 'noInput');
  const applyToAllText = 'apply those responses to all ' + loopType;
  const canAdvance = !missingValues && !hasErrors;
  const submit = () => onSubmit && onSubmit();
  // Enter on a simple input advances to the next item, mirroring the Next button.
  const handleEnter = () => {
    if (!readOnly && canAdvance) submit();
  };

  return (
    <div
      data-testid="form"
      className={cn(
        FORM_CLASS,
        readOnly && 'readOnly',
        highlighted && 'ring-2 ring-primary-900'
      )}
    >
      <div
        ref={refItem}
        className={cn(
          'item-title-bar flex items-center justify-between gap-4 px-5 py-3',
          readOnly ? 'bg-primary-950' : 'bg-primary-900'
        )}
      >
        <p className="itemTitle font-display text-lg font-semibold leading-tight tracking-tight text-on-primary">
          {item.title}
        </p>
      </div>
      <div className="px-5 pb-8 pt-5">
      {globalErrors.map((error, errorIndex) => (
        <WarningMessage key={'error-' + errorIndex} header="Inconsistent answers" message={error.text} />
      ))}
      {!readOnly && showSubmit && fields.length > 0 && (
        <div className="flex justify-end mb-2">
          <Button
            title="next"
            variant="outline"
            size="icon"
            onClick={submit}
            disabled={missingValues || hasErrors}
          >
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}
      <div data-testid="fields-group" className="space-y-4">
        {fields.map((field, fieldIndex) => (
          <ItemField
            readOnly={readOnly}
            isObserver={isObserver}
            handleChange={(value) => handleChange(fieldIndex, value)}
            handleAddition={(value) => handleAddition(fieldIndex, value)}
            togglePreliminary={() => togglePreliminary(fieldIndex)}
            onEnter={handleEnter}
            value={newValues[fieldIndex]}
            preliminary={newPreliminaries[fieldIndex]}
            preliminarySection={preliminarySection}
            error={fieldErrors[fieldIndex]}
            itemKey={itemKey + '-' + loopIndex}
            loopName={item.loopName}
            focused={autoFocusFirstField && fieldIndex === 0}
            key={itemKey + '-' + loopIndex + '-' + fieldIndex}
            {...field}
          />
        ))}
        {actionableFields.length > 0 && showApplyToAll && (
          <>
            <hr className="border-gray-200" />
            <ItemField
              type="checkbox"
              handleChange={(value) => onApplyToAll(value)}
              label={applyToAllText}
              readOnly={readOnly}
              isObserver={isObserver}
              value={applyToAll}
            />
          </>
        )}
      </div>
      <br />
      {!readOnly && showSubmit && fields.length > 0 && (
        <div className="flex justify-center mt-4">
          <Button onClick={submit} disabled={missingValues || hasErrors}>
            Next
          </Button>
        </div>
      )}
      <div className="flex justify-between mt-4">
        <span className="mandatory-field-explanation text-xs text-gray-500">
          <span className="text-danger-900">*</span> required fields
        </span>
        <span data-testid="item-id" className="text-xs text-gray-300">
          {item.id}
        </span>
      </div>
      </div>
    </div>
  );
}

export const Item = memo(ItemComponent, (prev, next) => (
  prev.item === next.item &&
  prev.applyToAll === next.applyToAll &&
  prev.showApplyToAll === next.showApplyToAll &&
  prev.loopIndex === next.loopIndex &&
  prev.loopType === next.loopType &&
  prev.isObserver === next.isObserver &&
  prev.values === next.values &&
  prev.preliminaries === next.preliminaries &&
  prev.hasErrors === next.hasErrors &&
  prev.preliminarySection === next.preliminarySection &&
  prev.readOnly === next.readOnly &&
  prev.showSubmit === next.showSubmit &&
  prev.autoFocusFirstField === next.autoFocusFirstField &&
  prev.highlighted === next.highlighted
));
